import React, { type FC, useContext, type PropsWithChildren, useState } from 'react';

import { useProductUrls, withImage, useSmartlingStrings } from '@lucid/hooks';
import { getZendeskLinks } from '@lucid/utils';
import qs from 'query-string';
import { getColor } from '@lucid/theme';

import reactHTMLParser from '../../utils/reactHTMLParser';
import Box from '../box/box';
import Heading from '../heading/heading';
import LayoutContext from '../context/layout-context';
import Link from '../link/link';
import MarketoForm from '../marketo-form/marketo-form';
import Paper from '../paper/paper';
import Container from '../container/container';
import Text from '../text/text';
import Image from '../image/image';
import type { Lucid } from '../../types/types';

export interface FormHeroProps {
  /** Type of form to send to Beacon */
  beaconFormType?: Lucid.Marketo.BeaconFormTypes;
  /** Sets the color of the icon. */
  backgroundColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** Allows the component to get additional styles. */
  className?: string;
  /** Heading for confirm state */
  confirmHeading?: string;
  /** Body for confirm state */
  confirmBody?: string;
  /** The CTA link and text for the confirm message. */
  confirmCta?: Lucid.Directus.Generated.Links;
  /** The button CTA link and text. */
  cta?: Lucid.Directus.Generated.Links;
  /** The heading text. */
  header?: string;
  /** The header variants. */
  headerVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** Image to be included in Hero */
  image?: Lucid.Directus.Generated.Directus_Files;
  /** Language Key from getLanguage */
  languageKey: Lucid.I18n.LanguagesShortCode;
  /** Location from template props */
  location: Location;
  /** marketoForm from pageContext */
  marketoForm?: Lucid.Marketo.FormInterface;
  /** determines what should be done when form is successful */
  onFormSuccess?: () => void;
  /** Determines if the marketo form should use the Paper component */
  shouldUsePaper?: boolean;
  /** The subheader text displayed under the heading text. */
  subheader?: string;
  /** The subheader text displayed under the heading text. */
  subheaderVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** Link to be displayed at the end of the subheader */
  subheaderCta?: Lucid.Directus.Generated.Links;
  /** Language Prefix for url from getLanguage */
  urlLangPrefix: Lucid.I18n.LanguagesURLPrefix;
}

const FormHero: FC<PropsWithChildren<FormHeroProps>> = ({
  beaconFormType = 'sales',
  backgroundColor,
  className,
  cta,
  confirmHeading,
  confirmBody,
  confirmCta,
  header = '',
  headerVariant = 'hero2XL700',
  image,
  languageKey,
  urlLangPrefix,
  location,
  marketoForm,
  onFormSuccess,
  shouldUsePaper,
  subheader = '',
  subheaderCta,
  subheaderVariant = 'paragraphM400',
  ...rest
}) => {
  const { shortCode, product } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_form_page_help', 'mktg_form_page_pricing_link', 'mktg_form_page_body_enterprise', 'mktg_form_page_h1'],
    shortCode,
  });

  const { helpCenterLink, supportLink } = getZendeskLinks(shortCode);
  const { pricingUrl } = useProductUrls(urlLangPrefix);
  const parsedQueryString = qs.parse(location?.search || '');
  const [isFormSuccessful, setIsFormSuccessful] = useState(parsedQueryString.skipForm === 'true');

  const handleOnFormSuccess = () => {
    if (onFormSuccess) {
      onFormSuccess();
    }
    setIsFormSuccessful(true);
  };

  return (
    <Box
      {...rest}
      css={theme => ({
        label: 'formHero',
        backgroundColor: getColor({ color: backgroundColor, theme }),
        paddingTop: theme.space.s40,
        display: 'flex',
        alignItems: 'start',
        flexDirection: 'column',
        [theme.breakpoints.bp1024.min]: {
          flexDirection: 'row',
        },
      })}
      // eslint-disable-next-line @stylistic/jsx-sort-props
      className={className}
    >
      <Container extraPadding={5}>
        {image && image.filename_disk && !isFormSuccessful && (
          <Box
            css={{
              maxWidth: '580px',
              margin: 'auto',
            }}
          >
            <Heading
              as="h1"
              css={{
                width: '100%',
                textAlign: 'center',
              }}
              dataElementPageLocation="hero"
              variant={headerVariant}
            >
              {header}
            </Heading>
            <Text
              css={{
                width: '100%',
                textAlign: 'center',
              }}
              dataElementPageLocation="hero"
              variant={subheaderVariant}
            >
              {subheader}
              {subheaderCta?.final_url && (
                <span>
                  {' '}
                  <Link href={subheaderCta.final_url} variant="primaryAlternate">
                    {subheaderCta?.default_link_text}
                  </Link>
                </span>
              )}
            </Text>
            <Box
              css={theme => [
                {
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  [theme.breakpoints.bp1024.min]: {
                    width: 'initial',
                  },
                },
              ]}
            >
              {cta?.final_url && (
                <Link displayAsButton href={cta.final_url}>
                  {cta.default_link_text}
                </Link>
              )}
            </Box>
          </Box>
        )}
        <Box
          css={theme => ({
            display: 'grid',
            gridTemplateColumns: '1fr',
            columnGap: theme.space.s80,
            width: '100%',
            marginBottom: theme.space.s80,
            [theme.breakpoints.bp900.min]: {
              gridTemplateColumns: 'minmax(0, 1fr) minmax(440px, 1fr)',
            },
          })}
        >
          {image?.filename_disk ? (
            <Box
              css={theme => ({
                whiteSpace: 'nowrap',
                textAlign: 'center',
                display: isFormSuccessful ? 'block' : 'none',
                [theme.breakpoints.bp900.min]: {
                  display: 'inherit',
                },
              })}
            >
              <span
                css={theme => ({
                  display: 'none',
                  [theme.breakpoints.bp900.min]: {
                    display: 'inline-block',
                    height: '100%',
                    verticalAlign: 'middle',
                  },
                })}
              />
              <Image
                css={theme => ({
                  maxHeight: '360px',
                  [theme.breakpoints.bp900.min]: {
                    maxHeight: 'inherit',
                    verticalAlign: 'middle',
                    maxWidth: '100%',
                  },
                })}
                dataElementPageLocation="hero"
                imageAlt=""
                imageSrc={withImage(image)}
              />
            </Box>
          ) : (
            <Box
              css={theme => ({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
                marginBottom: theme.space.s120,
                [theme.breakpoints.bp1024.min]: {
                  marginBottom: 0,
                  marginTop: theme.space.s24,
                  paddingRight: theme.space.s80,
                  alignItems: 'start',
                },
              })}
            >
              <Heading
                as="h1"
                css={theme => ({
                  textAlign: 'center',
                  width: '100%',
                  [theme.breakpoints.bp1024.min]: {
                    textAlign: 'left',
                  },
                })}
                dataElementPageLocation="hero"
                variant={headerVariant}
              >
                {header}
              </Heading>
              <Text
                css={theme => ({
                  width: '100%',
                  textAlign: 'center',
                  [theme.breakpoints.bp1024.min]: {
                    textAlign: 'left',
                  },
                })}
                dataElementPageLocation="hero"
                variant={subheaderVariant}
              >
                {subheader}
                {subheaderCta?.final_url && (
                  <span>
                    {' '}
                    <Link href={subheaderCta.final_url} variant="secondary">
                      {subheaderCta?.default_link_text}
                    </Link>
                  </span>
                )}
              </Text>
              <Box
                css={theme => [
                  {
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    [theme.breakpoints.bp1024.min]: {
                      width: 'initial',
                    },
                  },
                ]}
              >
                {cta?.final_url && (
                  <Link displayAsButton href={cta.final_url}>
                    {cta.default_link_text}
                  </Link>
                )}
              </Box>
            </Box>
          )}
          <Box
            css={theme => [
              {
                width: '100%',
                maxWidth: '540px',
                margin: '0 auto',
                paddingTop: theme.space.s48,
                [theme.breakpoints.bp900.min]: {
                  display: 'block',
                  margin: '0 auto',
                },
              },
              isFormSuccessful && {
                '& > div:first-of-type': {
                  height: `calc(100% - ${theme.space.s48}px)`,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  textAlign: 'center',
                  [theme.breakpoints.bp900.min]: { textAlign: 'left', maxWidth: '480px' },
                },
                [theme.breakpoints.bp900.min]: { paddingTop: 0 },
              },
            ]}
          >
            {shouldUsePaper ? (
              <Paper
                css={theme => ({
                  [theme.breakpoints.bp500.max]: {
                    border: 0,
                    padding: 0,
                    boxShadow: 'none',
                    background: 'none',
                    overflow: 'visible',
                  },
                })}
                variant="raised"
              >
                <MarketoForm
                  hasFullWidthSubmit
                  isFullWidth
                  shouldOnlyUseChildren
                  beaconFormType={beaconFormType}
                  form={marketoForm}
                  languageKey={languageKey}
                  location={location}
                  onSuccess={handleOnFormSuccess}
                  product={product}
                  topText={{
                    body: `<h1 style="margin-top:0; font-size: 1.5rem">${withSmartling('mktg_form_page_h1')}</h1>
                      <p>${withSmartling('mktg_form_page_body_enterprise')} ${withSmartling(
                        'mktg_form_page_pricing_link',
                        {
                          url: pricingUrl,
                        },
                      )}</p>`,
                  }}
                >
                  <Box>
                    <Heading as="h1">{reactHTMLParser(confirmHeading)}</Heading>
                    <Text>{reactHTMLParser(confirmBody)}</Text>
                    {confirmCta?.final_url && (
                      <Link href={confirmCta?.final_url} iconAnimation="navigational" variant="primaryAlternate">
                        {confirmCta?.default_link_text}
                      </Link>
                    )}
                  </Box>
                </MarketoForm>
              </Paper>
            ) : (
              <MarketoForm
                hasFullWidthSubmit
                isFullWidth
                shouldHideTopText
                shouldOnlyUseChildren
                beaconFormType={beaconFormType}
                form={marketoForm}
                languageKey={languageKey}
                location={location}
                onSuccess={handleOnFormSuccess}
                product={product}
              >
                <Box>
                  <Heading as="h1" variant="heading2XL">
                    {reactHTMLParser(confirmHeading)}
                  </Heading>
                  <Text variant="paragraphM400">{reactHTMLParser(confirmBody)}</Text>
                  {confirmCta?.final_url && (
                    <Link href={confirmCta?.final_url} iconAnimation="navigational" variant="primaryAlternate">
                      {confirmCta?.default_link_text}
                    </Link>
                  )}
                </Box>
              </MarketoForm>
            )}
            {!isFormSuccessful && (
              <Box
                css={theme => [
                  theme.shared.paragraphNestedLinkStyles(theme, 'primaryAlternate'),
                  {
                    borderRadius: theme.radii.r4,
                    border: backgroundColor ? '' : `1px solid ${theme.colors.coolGrey[5]}`,
                    fontSize: '90%',
                    marginTop: theme.space.s32,
                  },
                ]}
              >
                {reactHTMLParser(
                  withSmartling('mktg_form_page_help', {}, [
                    `<a href="${helpCenterLink}">{}</a>`,
                    `<a href="${supportLink}">{}</a>`,
                  ]),
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export { FormHero };
export default FormHero;
